import React, { useState, useEffect, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import toast from 'react-hot-toast';
import { authState } from '../../store/atoms';
import { lessonAPI } from '../../services';
import TeacherPageHeader from '../../components/TeacherPageHeader';
import { LessonRequest, LessonApprovalRequest, Teacher } from '../../types/api';
import Modal from '../../components/Modal';
import CustomSelect from '../../components/CustomSelect';
import Tabs from '../../components/Tabs';
import { formatPhoneNumber, formatDateShort, formatBirthDate, getLessonTypeLabel } from '../../utils/formatters';
import { parsePreferredTimesForLesson } from '../../utils/parsers';
import { getEndTime } from '../../utils/dateUtils';
import { LESSON_FEES, STATUS_LABELS, WEEKDAY_OPTIONS } from '../../config/constants';
import { useModal } from '../../hooks/common';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { copyTemporaryPassword } from '../../utils/clipboard';
import { PageLoading, EmptyState } from '../../components';

const LessonRequests: React.FC = () => {
  const auth = useAtomValue(authState);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // 상태별 건수 (시안: 전체 12 / 대기 3 / 승인 8 / 거절 1)
  const [statusCounts, setStatusCounts] = useState<{ all?: number; pending?: number; approved?: number; rejected?: number }>({});

  const loadStatusCounts = useCallback(async () => {
    try {
      const [all, pending, approved, rejected] = await Promise.all([
        lessonAPI.getRequests({ limit: 1, offset: 0 }),
        lessonAPI.getRequests({ status: 'pending', limit: 1, offset: 0 }),
        lessonAPI.getRequests({ status: 'approved', limit: 1, offset: 0 }),
        lessonAPI.getRequests({ status: 'rejected', limit: 1, offset: 0 }),
      ]);
      setStatusCounts({
        all: all.data?.pagination?.total,
        pending: pending.data?.pagination?.total,
        approved: approved.data?.pagination?.total,
        rejected: rejected.data?.pagination?.total,
      });
    } catch {
      // 건수 조회 실패는 무시
    }
  }, []);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  // Infinite scroll hook
  const {
    items: requests,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    handleScroll
  } = useInfiniteScroll<LessonRequest>({
    fetchFunction: lessonAPI.getRequests,
    limit: 200,
    searchTerm,
    statusFilter: selectedStatus
  });

  const statusTabOptions = [
    { value: 'all', label: '전체', count: statusCounts.all },
    { value: 'pending', label: '대기', count: statusCounts.pending },
    { value: 'approved', label: '승인', count: statusCounts.approved },
    { value: 'rejected', label: '거절', count: statusCounts.rejected },
  ];

  const approvalModal = useModal<LessonRequest>();
  const rejectionModal = useModal<LessonRequest>();
  const detailModal = useModal<LessonRequest>();
  const isMobile = useIsMobile();

  // 데스크탑: 시안처럼 테이블 아래 인라인 상세 패널
  const [selectedDetail, setSelectedDetail] = useState<LessonRequest | null>(null);

  const openDetail = (request: LessonRequest) => {
    if (isMobile) {
      detailModal.open(request);
    } else {
      setSelectedDetail(request);
    }
  };

  // 기본으로 가장 최신 요청의 상세 표시 (목록 갱신 시 유지, 없어지면 최신으로)
  useEffect(() => {
    if (isMobile || requests.length === 0) return;
    setSelectedDetail(prev =>
      prev && requests.some(r => r.id === prev.id)
        ? requests.find(r => r.id === prev.id) || requests[0]
        : requests[0]
    );
  }, [requests, isMobile]);

  const [isProcessing, setIsProcessing] = useState(false);

  // 일정 확정 모달 관련 상태
  const [selectedScheduleOption, setSelectedScheduleOption] = useState<'preferred' | 'custom'>('preferred');
  const [selectedDateTime, setSelectedDateTime] = useState('');

  // 승인 폼 데이터
  const [approvalData, setApprovalData] = useState<LessonApprovalRequest>({
    teacherId: '',
    teacherResponse: '',
    assignedTime: '',
    lessonDayOfWeek: 'monday', // 기본값 (사용자가 변경 가능)
    lessonStartTime: '',
    lessonEndTime: '',
    monthlyFee: 0,
    isForeigner: false
  });

  // 수업 유형에 따른 기본 월 수업료 설정
  const getDefaultMonthlyFee = (lessonType: string) => {
    switch (lessonType) {
      case '입시':
        return LESSON_FEES.ENTRANCE_EXAM;
      case '일반':
      default:
        return LESSON_FEES.REGULAR;
    }
  };

  const [rejectionReason, setRejectionReason] = useState('');

  const statusLabels = STATUS_LABELS.LESSON_REQUEST;

  const handleApprove = (request: LessonRequest) => {
    // 승인 시 필요한 기본 데이터만 설정
    setApprovalData({
      teacherId: auth?.userType === 'teacher' && auth?.userInfo ? (auth.userInfo as Teacher).teacherId : '',
      teacherResponse: '수업 신청이 승인되었습니다.',
      assignedTime: request.preferredTime,
      lessonDayOfWeek: 'monday', // 기본값 (사용자가 변경 가능)
      lessonStartTime: '',
      lessonEndTime: '',
      monthlyFee: getDefaultMonthlyFee(request.lessonType), // 수업 유형에 따른 기본 월 수업료
      isForeigner: request.isForeigner
    });
    setSelectedScheduleOption('preferred');
    setSelectedDateTime('');
    approvalModal.open(request);
  };

  const handleReject = (request: LessonRequest) => {
    setRejectionReason('');
    rejectionModal.open(request);
  };

  const submitApproval = async () => {
    if (!approvalModal.data) return;

    try {
      setIsProcessing(true);

      // 선택된 시간이 있으면 요일과 시간을 자동으로 설정
      if (selectedScheduleOption === 'preferred' && selectedDateTime) {
        try {
          const selectedOption = JSON.parse(selectedDateTime);

          const updatedApprovalData = {
            ...approvalData,
            lessonDayOfWeek: selectedOption.dayOfWeek as "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday",
            lessonStartTime: selectedOption.time, // HH:mm 형식
            lessonEndTime: getEndTime(selectedOption.time) // 1시간 후
          };

          const response = await lessonAPI.approveRequest(approvalModal.data.requestId || approvalModal.data.id.toString(), updatedApprovalData);
          if (response.success) {
            const { temporaryPassword, studentName, userId } = response.data;

            // 임시 비밀번호를 클립보드에 복사
            await copyTemporaryPassword(temporaryPassword, studentName, userId);

            approvalModal.close();
            refresh(); // 목록 새로고침
            loadStatusCounts();
            setSelectedDetail(null);
          } else {
            toast.error('승인 처리 중 오류가 발생했습니다: ' + response.message);
          }
        } catch {
          toast.error('선택된 시간 정보를 처리할 수 없습니다. 기타 옵션을 사용해주세요.');
        }
      } else {
        // 직접 입력한 경우 기존 로직 사용
        const response = await lessonAPI.approveRequest(approvalModal.data.requestId || approvalModal.data.id.toString(), approvalData);
        if (response.success) {
          const { temporaryPassword, studentName, userId } = response.data;

          // 임시 비밀번호를 클립보드에 복사
          await copyTemporaryPassword(temporaryPassword, studentName, userId);

          approvalModal.close();
          refresh(); // 목록 새로고침
          loadStatusCounts();
          setSelectedDetail(null);

          // 사이드바 카운트 새로고침
          window.dispatchEvent(new Event('refreshPendingCounts'));
        } else {
          toast.error('승인 처리 중 오류가 발생했습니다: ' + response.message);
        }
      }
    } catch {
      toast.error('승인 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  const submitRejection = async () => {
    if (!rejectionModal.data || !rejectionReason.trim()) {
      toast.error('거절 사유를 입력해주세요.');
      return;
    }

    try {
      setIsProcessing(true);
      const response = await lessonAPI.rejectRequest(rejectionModal.data.requestId || rejectionModal.data.id.toString(), rejectionReason);

      if (response.success) {
        toast.success('수업 신청이 거절되었습니다.');
        rejectionModal.close();
        refresh(); // 목록 새로고침
        loadStatusCounts();
        setSelectedDetail(null);

        // 사이드바 카운트 새로고침
        window.dispatchEvent(new Event('refreshPendingCounts'));
      } else {
        toast.error('거절 처리 중 오류가 발생했습니다: ' + response.message);
      }
    } catch {
      toast.error('거절 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading && requests.length === 0) {
    return <PageLoading message="정규수업 신청 목록을 불러오는 중..." fullScreen />;
  }

  return (
    <div className="teacher-page list-detail-page">
      {/* 상단 헤더 바 (데스크탑) — 타이틀 + 상태 필터 + 검색 */}
      <TeacherPageHeader title="수업신청관리">
        <Tabs
          options={statusTabOptions}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
        <input
          className="header-search"
          type="text"
          placeholder="이름·연락처 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </TeacherPageHeader>

      {/* 필터 (모바일 전용) */}
      <div className="content-header desktop-hidden">
        <Tabs
          options={[
            { value: 'all', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'approved', label: '승인됨' },
            { value: 'rejected', label: '거절됨' }
          ]}
          value={selectedStatus}
          onChange={(value) => setSelectedStatus(value)}
        />
      </div>

      {/* 신청 목록 */}
      <div className="requests-container" onScroll={handleScroll}>
        {error && (
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            {error}
          </div>
        )}
        {requests.length === 0 && !isLoading ? (
          <EmptyState
            title="신청된 정규수업이 없습니다"
            description="새로운 신청이 들어오면 여기에 표시됩니다."
          />
        ) : (
          <>
            {/* 데스크탑 테이블 뷰 */}
            <div className="table-view">
              <div className="table-wrapper">
                <table className="table lesson-requests-table">
                  <thead>
                    <tr>
                      <th>이름</th>
                      <th>연락처</th>
                      <th>희망 시간</th>
                      <th>신청일</th>
                      <th>상태</th>
                      <th style={{ textAlign: 'right' }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} className={`status-row status-${request.status}`} onClick={() => openDetail(request)}>
                        <td>
                          <div className="name-cell">
                            <span className="name">{request.name}</span>
                            <span className="sub">{formatBirthDate(request.birthDate)}</span>
                          </div>
                        </td>
                        <td>{formatPhoneNumber(request.phoneNumber)}</td>
                        <td className="time-cell">
                          {request.preferredTime.length > 30 ? `${request.preferredTime.substring(0, 30)}...` : request.preferredTime}
                        </td>
                        <td>{formatDateShort(request.createdAt)}</td>
                        <td>
                          <span className={`status-badge ${request.status}`}>
                            {statusLabels[request.status as keyof typeof statusLabels] || request.status}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {request.status === 'pending' ? (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleApprove(request)}
                                disabled={isProcessing}
                              >
                                승인
                              </button>
                              <button
                                className="btn-action reject"
                                onClick={() => handleReject(request)}
                                disabled={isProcessing}
                              >
                                거절
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <span className="done-label">처리 완료</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 카드 뷰 */}

            <div className="card-view">
              <div className="requests-list">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className={`request-item status-${request.status}`}
                    onClick={() => detailModal.open(request)}
                  >
                    <div className="item-row">
                      <span className="item-name">{request.name}</span>
                      <span className="item-phone">{formatPhoneNumber(request.phoneNumber)}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-time">{request.preferredTime.length > 25 ? `${request.preferredTime.substring(0, 25)}...` : request.preferredTime}</span>
                      <span className="item-date">{formatDateShort(request.createdAt)}</span>
                    </div>
                    {request.status === 'pending' && (
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        <button className="btn-approve" onClick={() => handleApprove(request)} disabled={isProcessing}>승인</button>
                        <button className="btn-reject" onClick={() => handleReject(request)} disabled={isProcessing}>거절</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div className="spinner">추가 데이터 로딩 중...</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 데스크탑 하단 고정 상세 (시안: 신청 상세 패널) */}
      {selectedDetail && (
        <div className="request-detail-inline table-view">
          <div className="detail-main">
            <div className="detail-title-row">
              <div className="detail-title">신청 상세 — {selectedDetail.name}</div>
              <span className={`status-badge ${selectedDetail.status}`}>
                {statusLabels[selectedDetail.status as keyof typeof statusLabels] || selectedDetail.status}
              </span>
              {selectedDetail.isForeigner && <span className="badge badge-secondary">Foreign</span>}
              {selectedDetail.status === 'pending' && (
                <div className="title-actions">
                  <button className="mini-btn primary" onClick={() => handleApprove(selectedDetail)} disabled={isProcessing}>
                    승인하기
                  </button>
                  <button className="mini-btn danger" onClick={() => handleReject(selectedDetail)} disabled={isProcessing}>
                    거절하기
                  </button>
                </div>
              )}
              <button className="detail-close" onClick={() => setSelectedDetail(null)} aria-label="닫기">✕</button>
            </div>
            <div className="detail-grid">
              <div>
                <div className="dt">신청 수업</div>
                <div className="dd">{getLessonTypeLabel(selectedDetail.lessonType)}</div>
              </div>
              <div>
                <div className="dt">희망 일정</div>
                <div className="dd">{selectedDetail.preferredTime}</div>
              </div>
              <div>
                <div className="dt">생년월일 · 성별</div>
                <div className="dd">
                  {formatBirthDate(selectedDetail.birthDate)} · {selectedDetail.gender === 'male' ? '남성' : selectedDetail.gender === 'female' ? '여성' : '기타'}
                </div>
              </div>
              <div>
                <div className="dt">연락처</div>
                <div className="dd">{formatPhoneNumber(selectedDetail.phoneNumber)}</div>
              </div>
            </div>
            {selectedDetail.message && (
              <div className="detail-message">{selectedDetail.message}</div>
            )}
          </div>
        </div>
      )}

      {/* 승인 모달 */}
      <Modal
        isOpen={approvalModal.isOpen}
        onClose={approvalModal.close}
        title={approvalModal.data ? `수업 신청 승인 - ${approvalModal.data.name}` : '수업 신청 승인'}
        size="md"
        width="800px"
      >
        {approvalModal.data && (
          <div className="approval-content">
            <div className="approval-form">
              <div className="schedule-options">
                <div className="option-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="scheduleOption"
                      value="preferred"
                      checked={selectedScheduleOption === 'preferred'}
                      onChange={() => setSelectedScheduleOption('preferred')}
                    />
                    <span>학생 희망 시간에서 선택</span>
                  </label>

                  {selectedScheduleOption === 'preferred' && approvalModal.data && (
                    <div className="preferred-times">
                      {parsePreferredTimesForLesson(approvalModal.data.preferredTime).map((option, index) => (
                        <label key={index} className="time-option">
                          <input
                            type="radio"
                            name="preferredDateTime"
                            value={JSON.stringify(option)}
                            checked={selectedDateTime === JSON.stringify(option)}
                            onChange={(e) => setSelectedDateTime(e.target.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                      {parsePreferredTimesForLesson(approvalModal.data.preferredTime).length === 0 && (
                        <p className="no-preferred-times">파싱 가능한 희망 시간이 없습니다. 기타 옵션을 선택해주세요.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="option-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="scheduleOption"
                      value="custom"
                      checked={selectedScheduleOption === 'custom'}
                      onChange={() => setSelectedScheduleOption('custom')}
                    />
                    <span>기타 (직접 입력)</span>
                  </label>

                  {selectedScheduleOption === 'custom' && (
                    <div className="custom-datetime">
                      <div className="form-group">
                        <label>수업 요일 *</label>
                        <CustomSelect
                          options={WEEKDAY_OPTIONS as any}
                          value={approvalData.lessonDayOfWeek}
                          onChange={(value) => setApprovalData({
                            ...approvalData,
                            lessonDayOfWeek: value as any
                          })}
                          placeholder="요일 선택"
                          className="form-select"
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>수업 시작 시간 *</label>
                          <input
                            type="time"
                            value={approvalData.lessonStartTime}
                            onChange={(e) => setApprovalData({
                              ...approvalData,
                              lessonStartTime: e.target.value
                            })}
                            className="form-input"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>수업 종료 시간 *</label>
                          <input
                            type="time"
                            value={approvalData.lessonEndTime}
                            onChange={(e) => setApprovalData({
                              ...approvalData,
                              lessonEndTime: e.target.value
                            })}
                            className="form-input"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>월 수업료</label>
                <input
                  type="number"
                  value={approvalData.monthlyFee}
                  onChange={(e) => setApprovalData({
                    ...approvalData,
                    monthlyFee: Number(e.target.value)
                  })}
                  className="form-input"
                  placeholder="예: 170000"
                />
              </div>

              <div className="form-group">
                <label>승인 메시지</label>
                <textarea
                  value={approvalData.teacherResponse}
                  onChange={(e) => setApprovalData({
                    ...approvalData,
                    teacherResponse: e.target.value
                  })}
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={submitApproval}
                disabled={
                  isProcessing ||
                  (selectedScheduleOption === 'preferred' && !selectedDateTime) ||
                  (selectedScheduleOption === 'custom' && (!approvalData.lessonStartTime || !approvalData.lessonEndTime))
                }
              >
                {isProcessing ? '처리중...' : '승인하기'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={approvalModal.close}
                disabled={isProcessing}
              >
                취소
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* 거절 모달 */}
      <Modal
        isOpen={rejectionModal.isOpen}
        onClose={rejectionModal.close}
        title={rejectionModal.data ? `수업 신청 거절 - ${rejectionModal.data.name}` : '수업 신청 거절'}
        size="sm"
        width="600px"
      >
        {rejectionModal.data && (
          <div className="rejection-content">
            <div className="rejection-info">
              <h4>{rejectionModal.data.name}님의 수업 신청</h4>
              <p><strong>희망 시간:</strong> {rejectionModal.data.preferredTime}</p>
              <p><strong>수업 유형:</strong> {getLessonTypeLabel(rejectionModal.data.lessonType)}</p>
            </div>

            <div className="form-group">
              <label>거절 사유 *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="form-textarea"
                rows={4}
                placeholder="거절 사유를 입력해주세요..."
                required
              />
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={rejectionModal.close}
                disabled={isProcessing}
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={submitRejection}
                disabled={isProcessing || !rejectionReason.trim()}
              >
                {isProcessing ? '처리중...' : '거절하기'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* 상세보기 모달 */}
      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.close}
        title="수업 신청 상세"
        size="sm"
      >
        {detailModal.data && (
          <div className="detail-content compact">
            <div className="detail-header">
              <div className="name-row">
                <span className="student-name">{detailModal.data.name}</span>
                {detailModal.data.isForeigner && <span className="badge-foreign">Foreign</span>}
                <span className={`status-badge ${detailModal.data.status}`}>
                  {statusLabels[detailModal.data.status as keyof typeof statusLabels] || detailModal.data.status}
                </span>
              </div>
              <div className="meta-info">
                {formatBirthDate(detailModal.data.birthDate)} · {detailModal.data.gender === 'male' ? '남성' : detailModal.data.gender === 'female' ? '여성' : '기타'} · {getLessonTypeLabel(detailModal.data.lessonType)}
              </div>
            </div>

            <div className="detail-info-list">
              <div className="info-row">
                <span className="label">연락처</span>
                <span className="value">{formatPhoneNumber(detailModal.data.phoneNumber)}</span>
              </div>
              {detailModal.data.email && (
                <div className="info-row">
                  <span className="label">이메일</span>
                  <span className="value">{detailModal.data.email}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">희망시간</span>
                <span className="value">{detailModal.data.preferredTime}</span>
              </div>
              {detailModal.data.message && (
                <div className="info-row">
                  <span className="label">문의</span>
                  <span className="value">{detailModal.data.message}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">신청일</span>
                <span className="value">{formatDateShort(detailModal.data.createdAt)}</span>
              </div>
            </div>

            {detailModal.data.status === 'pending' && (
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    detailModal.close();
                    handleApprove(detailModal.data!);
                  }}
                >
                  승인
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    detailModal.close();
                    handleReject(detailModal.data!);
                  }}
                >
                  거절
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LessonRequests; 